import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChallengeAssessment = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});

  const assessmentQuestions = [
    {
      id: 'velocity',
      title: 'Engineering Velocity Assessment',
      description: 'How would you rate your current development velocity?',
      type: 'scale',
      options: [
        { value: 1, label: 'Significantly Behind Schedule', description: 'Consistently missing deadlines, features taking 2-3x longer than estimated' },
        { value: 2, label: 'Moderately Behind', description: 'Some delays, occasional missed milestones, velocity inconsistent' },
        { value: 3, label: 'Meeting Expectations', description: 'Generally on track, predictable delivery, room for improvement' },
        { value: 4, label: 'Exceeding Expectations', description: 'Consistently delivering ahead of schedule, high team productivity' },
        { value: 5, label: 'Industry Leading', description: 'Exceptional velocity, continuous delivery, benchmark for others' }
      ]
    },
    {
      id: 'technical_debt',
      title: 'Technical Debt Assessment',
      description: 'What is your current technical debt situation?',
      type: 'multiple',
      options: [
        { value: 'legacy_systems', label: 'Legacy Systems', description: 'Outdated technologies slowing development' },
        { value: 'poor_architecture', label: 'Architectural Issues', description: 'Monolithic or poorly designed systems' },
        { value: 'code_quality', label: 'Code Quality Problems', description: 'Lack of standards, testing, or documentation' },
        { value: 'infrastructure', label: 'Infrastructure Debt', description: 'Manual processes, poor CI/CD, scaling issues' },
        { value: 'minimal_debt', label: 'Minimal Technical Debt', description: 'Well-maintained codebase with good practices' }
      ]
    },
    {
      id: 'ownership_culture',
      title: 'Team Ownership Culture',
      description: 'How would you describe your team\'s ownership mentality?',
      type: 'single',
      options: [
        { value: 'reactive', label: 'Reactive Culture', description: 'Team waits for direction, limited initiative, blame-focused' },
        { value: 'process_driven', label: 'Process-Driven', description: 'Follows procedures but limited ownership beyond tasks' },
        { value: 'collaborative', label: 'Collaborative Ownership', description: 'Shared responsibility, good communication, some initiative' },
        { value: 'proactive', label: 'Proactive Ownership', description: 'Team takes initiative, owns outcomes, continuous improvement' }
      ]
    },
    {
      id: 'scalability',
      title: 'Architectural Scalability',
      description: 'How well does your current architecture handle growth?',
      type: 'single',
      options: [
        { value: 'struggling', label: 'Struggling to Scale', description: 'Performance issues, frequent outages, manual scaling' },
        { value: 'limited', label: 'Limited Scalability', description: 'Can handle current load but concerns about growth' },
        { value: 'adequate', label: 'Adequate Scaling', description: 'Handles growth reasonably well with some manual intervention' },
        { value: 'excellent', label: 'Excellent Scalability', description: 'Auto-scaling, resilient architecture, handles growth seamlessly' }
      ]
    }
  ];

  const handleResponse = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < assessmentQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(responses);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentQuestion = assessmentQuestions[currentStep];
  const isLastStep = currentStep === assessmentQuestions.length - 1;
  const hasResponse = responses[currentQuestion.id];

  const renderOptions = () => {
    switch (currentQuestion.type) {
      case 'scale':
        return (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => (
              <div
                key={option.value}
                onClick={() => handleResponse(currentQuestion.id, option.value)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  responses[currentQuestion.id] === option.value
                    ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50 hover:bg-muted'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-text-primary">{option.label}</span>
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-3 h-3 rounded-full ${
                          i < option.value ? 'bg-accent' : 'bg-border'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-text-secondary">{option.description}</p>
              </div>
            ))}
          </div>
        );

      case 'multiple':
        return (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const isSelected = Array.isArray(responses[currentQuestion.id]) 
                ? responses[currentQuestion.id].includes(option.value)
                : false;
              
              return (
                <div
                  key={option.value}
                  onClick={() => {
                    const current = responses[currentQuestion.id] || [];
                    const newValue = isSelected
                      ? current.filter(v => v !== option.value)
                      : [...current, option.value];
                    handleResponse(currentQuestion.id, newValue);
                  }}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50 hover:bg-muted'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                      isSelected ? 'border-accent bg-accent' : 'border-border'
                    }`}>
                      {isSelected && <Icon name="Check" size={12} color="white" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-text-primary mb-1">{option.label}</h4>
                      <p className="text-sm text-text-secondary">{option.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'single':
        return (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => (
              <div
                key={option.value}
                onClick={() => handleResponse(currentQuestion.id, option.value)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  responses[currentQuestion.id] === option.value
                    ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50 hover:bg-muted'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    responses[currentQuestion.id] === option.value ? 'border-accent bg-accent' : 'border-border'
                  }`}>
                    {responses[currentQuestion.id] === option.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-text-primary mb-1">{option.label}</h4>
                    <p className="text-sm text-text-secondary">{option.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-text-primary">
            Step {currentStep + 1} of {assessmentQuestions.length}
          </span>
          <span className="text-sm text-text-secondary">
            {Math.round(((currentStep + 1) / assessmentQuestions.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-accent h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / assessmentQuestions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          {currentQuestion.title}
        </h3>
        <p className="text-text-secondary">
          {currentQuestion.description}
        </p>
      </div>

      {/* Options */}
      <div className="mb-8">
        {renderOptions()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentStep === 0}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>

        <div className="flex items-center space-x-2">
          {assessmentQuestions.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full ${
                index === currentStep
                  ? 'bg-accent'
                  : index < currentStep
                  ? 'bg-success' :'bg-border'
              }`}
            />
          ))}
        </div>

        <Button
          variant="default"
          onClick={handleNext}
          disabled={!hasResponse}
          iconName={isLastStep ? "Check" : "ChevronRight"}
          iconPosition="right"
          className="bg-accent hover:bg-accent/90"
        >
          {isLastStep ? 'Complete Assessment' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default ChallengeAssessment;